import React, { useState, Dispatch, SetStateAction } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  Typography,
  Button,
  Box,
  TextField,
  Modal,
  CircularProgress,
  Divider,
} from '@mui/material';
import { loadableAccountInfoAtom } from '../../../atoms/accountInfoAtoms';
import { snackbarPropsAtom } from '../../../atoms';
import { CheckBoxList } from '../../../sharedComponents';
import { INDUSTRY } from '../../../constant';
import AccountDeleteModal from '../accountDeleteModal';

const ModalWrapper = React.forwardRef<
  typeof Box,
  { children: React.ReactNode }
>(({ children }, ref) => {
  return (
    <Box
      ref={ref}
      tabIndex={-1}
      flexDirection={'column'}
      rowGap={3}
      sx={{
        height: '90%',
        width: '60%',
        display: 'flex',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        minWidth: '500px',
        bgcolor: 'background.paper',
        border: 2,
        borderColor: 'black',
        borderRadius: '3px',
      }}
    >
      {children}
    </Box>
  );
});

const ModalHeader = () => {
  return (
    <Box
      display={'flex'}
      minHeight={'50px'}
      bgcolor={'#007DC5'}
      color={'white'}
      alignItems={'center'}
      boxShadow={2}
      padding={1}
    >
      <Typography variant="h5" marginLeft={2}>
        アカウント情報
      </Typography>
    </Box>
  );
};

const AccountInfoBox = () => {
  const value = useAtomValue(loadableAccountInfoAtom);
  const snackbarProps = useSetAtom(snackbarPropsAtom);

  let boxContent;
  // loading
  if (value.state === 'loading') {
    boxContent = (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // fetch error!
  if (value.state === 'hasError') {
    snackbarProps({
      isOpen: true,
      message: 'アカウント情報の取得に失敗しました',
      type: 'error',
    });

    boxContent = (
      <Typography> {'アカウント情報の取得に失敗しました'}</Typography>
    );
  }

  // get data
  if (value.state === 'hasData') {
    const account = value.data;
    boxContent = (
      <>
        <Box display="flex" flexDirection="row" width="100%" columnGap={6}>
          <TextField
            label={'姓'}
            variant="outlined"
            fullWidth
            InputProps={{
              readOnly: true,
            }}
            value={account.lastName}
          />
          <TextField
            label={'名'}
            variant="outlined"
            fullWidth
            value={account.firstName}
            InputProps={{
              readOnly: true,
            }}
          />
        </Box>
        <Divider variant="middle" />
        <TextField
          label={'会社名'}
          variant="outlined"
          fullWidth
          value={account.company}
          InputProps={{
            readOnly: true,
          }}
        />
        <TextField
          label={'電話番号'}
          variant="outlined"
          fullWidth
          value={account.phone}
          InputProps={{
            readOnly: true,
          }}
        />
        <Box display="flex" flexDirection="row" width="100%" columnGap={6}>
          <TextField
            label={'国'}
            variant="outlined"
            fullWidth
            value={account.country}
            InputProps={{
              readOnly: true,
            }}
          />
          <TextField
            label={'州'}
            variant="outlined"
            fullWidth
            value={account.state}
            InputProps={{
              readOnly: true,
            }}
          />
        </Box>
        <TextField
          label={'住所'}
          variant="outlined"
          fullWidth
          value={account.address}
          InputProps={{
            readOnly: true,
          }}
        />
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          justifyContent="center"
        >
          <Typography variant="body1" fontWeight="bold">
            {'職種'}
          </Typography>

          <CheckBoxList
            items={INDUSTRY}
            selected={account.industry}
            setSelected={() => {}}
          />
        </Box>
      </>
    );
  }

  return (
    <Box
      width={'75%'}
      height={'80%'}
      display="flex"
      flexDirection="column"
      alignItems="center"
      borderColor={'gray'}
      border={1}
      gap="30px"
      overflow={'auto'}
      padding={3}
    >
      {boxContent}
    </Box>
  );
};

const AccountDeleteButton = (props: {
  setIsAccountDeleteModalOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  const { setIsAccountDeleteModalOpen } = props;

  return (
    <Button
      variant="contained"
      color={'error'}
      sx={{ width: '200px' }}
      onClick={() => {
        setIsAccountDeleteModalOpen(true);
      }}
    >
      アカウント削除
    </Button>
  );
};

const AccountInfoModal = (props: {
  isAccountModalOpen: boolean;
  setIsAccountModalOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  const { isAccountModalOpen, setIsAccountModalOpen } = props;
  const [isAccountDeleteModalOpen, setIsAccountDeleteModalOpen] =
    useState(false);

  return (
    <>
      <Modal
        open={isAccountModalOpen}
        onClose={() => {
          setIsAccountModalOpen(!isAccountModalOpen);
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <ModalWrapper>
          <ModalHeader />
          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent={'flex-start'}
            flexDirection={'column'}
            overflow={'auto'}
            rowGap={5}
          >
            <AccountInfoBox />
            <Box
              display={'flex'}
              flexDirection={'row'}
              justifyContent={'center'}
              columnGap={10}
              marginBottom={5}
            >
              <AccountDeleteButton
                setIsAccountDeleteModalOpen={setIsAccountDeleteModalOpen}
              />
              <Button
                variant="contained"
                color={'inherit'}
                sx={{ width: '200px' }}
                onClick={() => {
                  setIsAccountModalOpen(false);
                }}
              >
                {'閉じる'}
              </Button>
            </Box>
          </Box>
        </ModalWrapper>
      </Modal>
      <AccountDeleteModal
        isAccountDeleteModalOpen={isAccountDeleteModalOpen}
        setIsAccountDeleteModalOpen={setIsAccountDeleteModalOpen}
        setIsAccountInfoModalOpen={setIsAccountModalOpen}
      />
    </>
  );
};

export default AccountInfoModal;
